/**
 * Month-token resolution for date interpolations.
 *
 * Import-free (only `Intl`), so it is SSR-safe, bundles into built pipes with
 * the rest of `interpolationFormat`, and is unit-testable under bare
 * `node --test` (`yarn test:unit`) — which cannot resolve the `@render/*`
 * alias, so `interpolationFormat.ts` itself can never be tested there.
 */

const SHORT_CACHE = new Map<string, Intl.DateTimeFormat>()
const LONG_CACHE = new Map<string, Intl.DateTimeFormat>()

function cached(
  cache: Map<string, Intl.DateTimeFormat>,
  locale: string,
  month: 'short' | 'long',
): Intl.DateTimeFormat {
  let f = cache.get(locale)
  if (!f) {
    // timeZone: 'UTC' for the same reason the full-date formatter pins it — a
    // YYYY-MM-DD seed is UTC midnight and must not drift a month boundary.
    f = new Intl.DateTimeFormat(locale, { month, timeZone: 'UTC' })
    cache.set(locale, f)
  }
  return f
}

const DIGITS_ONLY = /^\d+$/

const trimDot = (s: string) => s.replace(/\.$/, '')

/**
 * The month token for a `1 Jan 2026`-style date.
 *
 * `inContext` is what a full day+month+year `month: 'short'` request yields —
 * CLDR's *format* (in-context) abbreviation, which is the bare month NUMBER for
 * cs / sk / ja / lt. That rendered `1 6 2026`, with the month indistinguishable
 * from the day. When it comes back digits-only, fall back to the locale's
 * *standalone* abbreviation (cs → `čvn`), then to the standalone long name
 * (ja → `6月`, lt → `birželis`) for locales that have no alphabetic
 * abbreviation at all.
 *
 * A locale whose in-context abbreviation is already a word is returned
 * untouched, so de (`Juni`), ru (`июн.`), hu, lv, pl and en render exactly as
 * before — no existing pipe's output changes.
 *
 * Trailing `.` is stripped here (de `Jan.` → `Jan`); don't re-strip at the call
 * site.
 */
export function localeMonthToken(locale: string, dt: Date, inContext: string): string {
  if (!DIGITS_ONLY.test(inContext)) return trimDot(inContext)

  const standalone = trimDot(cached(SHORT_CACHE, locale, 'short').format(dt))
  if (!DIGITS_ONLY.test(standalone)) return standalone

  const long = trimDot(cached(LONG_CACHE, locale, 'long').format(dt))
  // Nothing alphabetic exists for this locale — keep the numeric token rather
  // than emitting an empty month.
  return DIGITS_ONLY.test(long) ? trimDot(inContext) : long
}
